import React, { useMemo, useState, memo, useCallback, useEffect } from 'react';
import { FileText, CheckCircle, Loader, Eye, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useSelector, useDispatch } from 'react-redux';
import { StepActionButton, DiscreteActionsDropdown } from '../ApplicationStepComponents';
import { useToast } from '@/components/hooks/use-toast';
import {
  selectIllustration,
  selectQuotes,
  selectSession,
  selectSessionAdvisor,
  putClientPolicyApplicationStepRequest,
  putMultipleFundingSourceForClientByClientIdRequest,
  postConsentRecordRequest,
  selectPostConsentRecordData,
  selectPostConsentRecordIsLoading,
  selectPostConsentRecordErrorMessage,
  clearPostConsentRecord,
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
} from '@client/model';
import { StepAlert } from '../ApplicationStepComponents';
import { UpdatedQuoteSection } from './UpdatedQuoteSection';
import { useIllustrationDownloadFlow } from '@client/hooks/useIllustrationDownloadFlow';
import { useIllustrationPolling } from '@client/hooks/useIllustrationPolling';
import { getQuoteId } from '@client/util/quoteUtils';
import { SessionType } from '@shared';
import {
  createUpdatedQuoteFromResponse,
  createQuoteWithROIandProjectedCoverage,
  createPolicyFundingSourceFromQuote,
} from '@client/util/policyQuoteCalculations';
import { ContactSupportButton } from '@client';
import { getUserIpAddress } from '@/util/getUserIpAddress';

interface DownloadPolicyIllustrationApplicationStepProps {
  clientPolicyApplicationId: string;
  onContinue: () => void;
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
  clientPartner?: Client | null;
  stepStatus: PolicyApplicationStep['status'];
  fundingSourceLabel: string;
  onStepSelect?: React.Dispatch<React.SetStateAction<string | null>>;
  client: Client;
  policyApplication: ClientPolicyApplication;
}

interface StepAction {
  type: 'primary' | 'secondary';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export const DownloadPolicyIllustrationApplicationStep: React.FC<DownloadPolicyIllustrationApplicationStepProps> =
  memo(
    ({
      clientPolicyApplicationId,
      onContinue,
      sourceQuote,
      clientPartner,
      stepStatus,
      fundingSourceLabel,
      client,
      policyApplication,
    }) => {
      const dispatch = useDispatch();
      const { toast } = useToast();
      const illustration = useSelector(selectIllustration);
      const quotes = useSelector(selectQuotes);
      const { sessionType } = useSelector(selectSession);
      const advisor = useSelector(selectSessionAdvisor);
      const [hasConfirmed, setHasConfirmed] = useState(false);

      // Consent recording state
      const postConsentRecordData = useSelector(selectPostConsentRecordData);
      const postConsentRecordIsLoading = useSelector(selectPostConsentRecordIsLoading);
      const postConsentRecordError = useSelector(selectPostConsentRecordErrorMessage);

      // Track if we're waiting to update funding source after consent
      const [pendingFundingSourceUpdate, setPendingFundingSourceUpdate] = useState<any>(null);

      useIllustrationPolling();

      useEffect(() => {
        dispatch(
          fetchApplicationConsentStatusByClientPolicyApplicationIdRequest({
            clientPolicyApplicationId,
            queryParams: { consentType: 'download' },
          }),
        );
      }, [clientPolicyApplicationId, dispatch]);

      const {
        hasIllustrationPdf,
        isCreatingIllustration,
        handleViewPdf,
        handleCreateIllustration,
      } = useIllustrationDownloadFlow(
        useMemo(
          () => ({
            quote: sourceQuote,
            selectedPremium: policyApplication.illustrationPremiumAmount,
            clientPartner,
            fundingSourceId: policyApplication.fundingSourceId,
          }),
          [
            sourceQuote,
            policyApplication.illustrationPremiumAmount,
            clientPartner,
            policyApplication.fundingSourceId,
          ],
        ),
      );

      const quoteId = useMemo(() => getQuoteId(sourceQuote), [sourceQuote]);

      const illustrationError = useMemo(() => {
        if (!illustration.erroredRequests || !quoteId) return null;
        return illustration.erroredRequests[quoteId];
      }, [illustration.erroredRequests, quoteId]);

      const hasIllustrationError = Boolean(
        illustrationError && illustrationError.errors.length > 0,
      );

      const handleDownloadOrViewPdf = useCallback(() => {
        if (hasIllustrationPdf) {
          handleViewPdf({ stopPropagation: () => {} } as React.MouseEvent);
        } else {
          handleCreateIllustration();
        }
      }, [hasIllustrationPdf, handleViewPdf, handleCreateIllustration]);

      const handleSkipStep = useCallback(() => {
        dispatch(
          putClientPolicyApplicationStepRequest({
            clientPolicyApplicationId,
            stepType: 'download-policy-illustration',
            status: 'skipped',
          }),
        );
        onContinue();
      }, [dispatch, clientPolicyApplicationId, onContinue]);

      const handleContinueWithIllustration = useCallback(async () => {
        if (sourceQuote && client && policyApplication) {
          const quoteId =
            'traditionalLTCQuoteId' in sourceQuote
              ? sourceQuote.traditionalLTCQuoteId
              : sourceQuote.insuranceQuoteId;
          const completedIllustration = illustration.completedIllustrations?.[quoteId];
          const fundingSourceId = policyApplication?.fundingSourceId;
          const shouldUpdateFundingSource =
            completedIllustration &&
            completedIllustration.fundingSourceId &&
            completedIllustration.responseData &&
            fundingSourceId &&
            completedIllustration.fundingSourceId === fundingSourceId && // Ensure IDs match
            client &&
            sourceQuote &&
            quotes.length > 0;

          const ipAddress = await getUserIpAddress();

          // Step 1: Record consent first
          dispatch(
            postConsentRecordRequest({
              clientId: client.clientId,
              clientPolicyApplicationId,
              resourceType: 'ClientPolicyApplicationConsents',
              consentId: 'advisorDownloadPolicyIllustration',
              ipAddress,
              userAgent: navigator.userAgent,
              consentType: 'download',
              metadata: {
                stepType: 'illustration-consent-form',
                clientPolicyApplicationId,
              },
            }),
          );

          // Store funding source update data for later use after consent succeeds
          if (shouldUpdateFundingSource) {
            const isAdvisorSession =
              sessionType === SessionType.advisor || sessionType === SessionType.wizard;
            const matchingQuote = quotes.find(q => q.insuranceQuoteId === quoteId);

            if (matchingQuote) {
              const updatedQuote = createUpdatedQuoteFromResponse(
                matchingQuote,
                completedIllustration.responseData || null,
              );
              const updatedQuoteWithROI = createQuoteWithROIandProjectedCoverage(
                updatedQuote,
                client,
              );
              const updatedFundingSource = createPolicyFundingSourceFromQuote(
                updatedQuote,
                client,
                isAdvisorSession,
                advisor?.advisorId,
                updatedQuoteWithROI,
                completedIllustration.pdfUrl,
              );

              setPendingFundingSourceUpdate({
                clientId: client.clientId,
                fundingSourceId: fundingSourceId,
                fundingSourceType: 'ltcPolicy',
                fundingSourceData: updatedFundingSource,
              });
            }
          } else {
            // If no funding source update needed, mark as null
            setPendingFundingSourceUpdate(null);
          }
        }
      }, [
        dispatch,
        clientPolicyApplicationId,
        onContinue,
        client,
        policyApplication,
        sourceQuote,
        quotes,
        sessionType,
        advisor,
        illustration.completedIllustrations,
      ]);

      // Effect to handle consent success and trigger funding source update
      useEffect(() => {
        let isMounted = true;

        if (postConsentRecordData && !postConsentRecordIsLoading) {
          // Consent recorded successfully
          const operations = [];

          // Step 2: Update funding source if needed
          if (pendingFundingSourceUpdate) {
            operations.push(
              dispatch(
                putMultipleFundingSourceForClientByClientIdRequest(pendingFundingSourceUpdate),
              ),
            );
          }

          // Step 3: Mark step as completed and continue
          operations.push(
            dispatch(
              putClientPolicyApplicationStepRequest({
                clientPolicyApplicationId,
                stepType: 'download-policy-illustration',
                status: 'completed',
              }),
            ),
          );

          // Execute operations in parallel
          Promise.allSettled(operations)
            .then(results => {
              if (!isMounted) return; // Prevent state updates if unmounted

              // Check if any operation failed
              const hasFailure = results.some(result => result.status === 'rejected');

              if (hasFailure) {
                toast({
                  title: 'Unable to Continue',
                  description: 'Please try again or contact support.',
                  variant: 'destructive',
                });
                return;
              }
              // Clear the pending update and consent record data
              setPendingFundingSourceUpdate(null);
              dispatch(clearPostConsentRecord());

              // Navigate to next step
              onContinue();
            })
            .catch(error => {
              if (!isMounted) return; // Prevent state updates if unmounted

              console.error('Failed to complete post-consent operations:', error);
              toast({
                title: 'Something went wrong',
                description: 'Please try again.',
                variant: 'destructive',
              });
            });
        }

        return () => {
          isMounted = false;
        };
      }, [
        postConsentRecordData,
        postConsentRecordIsLoading,
        pendingFundingSourceUpdate,
        dispatch,
        clientPolicyApplicationId,
        onContinue,
      ]);

      // Effect to handle consent recording errors
      useEffect(() => {
        if (postConsentRecordError && !postConsentRecordIsLoading) {
          // Clear the pending update on error
          setPendingFundingSourceUpdate(null);

          // Show error toast
          toast({
            title: 'Failed to Record Consent',
            description: 'There was an error recording your consent. Please try again.',
            variant: 'destructive',
          });

          // Clear the error so toast doesn't show again
          dispatch(clearPostConsentRecord());
        }
      }, [postConsentRecordError, postConsentRecordIsLoading, toast, dispatch]);

      const getButtonText = useCallback(() => {
        if (isCreatingIllustration) return 'Creating...';
        return 'Download PDF';
      }, [isCreatingIllustration, hasIllustrationPdf]);

      const actions = useMemo((): StepAction[] => {
        const actionsList: StepAction[] = [];

        if (stepStatus === 'completed') {
          return actionsList;
        }

        if (!hasIllustrationPdf) {
          actionsList.push({
            type: 'primary',
            label: getButtonText(),
            onClick: handleDownloadOrViewPdf,
            disabled: isCreatingIllustration || postConsentRecordIsLoading,
            isLoading: isCreatingIllustration,
          });
        }

        if (hasIllustrationPdf && hasConfirmed) {
          actionsList.push({
            type: 'secondary',
            label: postConsentRecordIsLoading ? 'Processing...' : 'Continue to Application',
            onClick: handleContinueWithIllustration,
            disabled: postConsentRecordIsLoading,
            isLoading: postConsentRecordIsLoading,
          });
        }
        return actionsList;
      }, [
        hasIllustrationPdf,
        hasConfirmed,
        stepStatus,
        getButtonText,
        handleDownloadOrViewPdf,
        handleContinueWithIllustration,
        isCreatingIllustration,
        postConsentRecordIsLoading,
      ]);

      const showFooter = useMemo(() => {
        return (
          actions.length > 0 ||
          hasIllustrationError ||
          (stepStatus !== 'skipped' &&
            stepStatus !== 'completed' &&
            sessionType === SessionType.wizard)
        );
      }, [actions.length, hasIllustrationError, stepStatus, sessionType]);

      return (
        <div className="p-2">
          <div className="mb-4">
            <h3 className="mb-1 text-lg font-semibold text-gray-900">
              Download Policy Illustration
            </h3>
            <p className="text-gray-600">
              {stepStatus === 'completed'
                ? `Downloaded and reviewed the policy illustration for your ${fundingSourceLabel} application.`
                : `Download and review the policy illustration to continue with your ${fundingSourceLabel} application. This document contains final rates and coverage details.`}
            </p>
          </div>

          <div className="mb-8">
            {/* Success message for completed step */}
            {stepStatus === 'completed' && (
              <div className="mb-4 text-sm text-green-600">
                ✓ Policy illustration was previously downloaded. You can view or re-download it
                below.
              </div>
            )}

            {/* Error Alert */}
            {hasIllustrationError && (
              <StepAlert
                title="We ran into an issue creating your policy illustration"
                description={`There was an error generating your policy illustration.`}
                variant="error"
              />
            )}

            {/* Download illustration section */}
            {!hasIllustrationError && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary-50 mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
                      <FileText className="text-primary h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 sm:text-base">
                        Policy Illustration
                      </h3>
                      <p className="mt-1 text-xs leading-snug text-gray-600 sm:text-sm">
                        Your personalized premium rates and coverage details
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:gap-3 sm:text-sm">
                    {isCreatingIllustration && (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <div>
                          <span>Generating...</span>
                          <span className="block text-[11px] text-gray-400 sm:text-xs">
                            This may take a few minutes
                          </span>
                        </div>
                      </>
                    )}
                    {hasIllustrationPdf && !isCreatingIllustration && (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Ready</span>
                      </>
                    )}
                    {hasIllustrationPdf && (
                      <Button
                        onClick={handleViewPdf}
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs sm:h-6 sm:px-3"
                      >
                        <Eye className="mr-1 h-3 w-3" />
                        View PDF
                      </Button>
                    )}
                    {!hasIllustrationPdf &&
                      stepStatus === 'completed' &&
                      !isCreatingIllustration && (
                        <Button
                          onClick={handleDownloadOrViewPdf}
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs sm:h-6 sm:px-3"
                          disabled={isCreatingIllustration}
                        >
                          Download PDF
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            )}

            {hasIllustrationPdf && stepStatus !== 'completed' && (
              <UpdatedQuoteSection
                clientPolicyApplicationId={clientPolicyApplicationId}
                sourceQuote={sourceQuote}
              />
            )}

            {hasIllustrationPdf && stepStatus !== 'completed' && (
              <div className="mb-4 mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="confirm-documents"
                    checked={hasConfirmed}
                    onCheckedChange={checked => setHasConfirmed(!!checked)}
                    className="mt-1"
                  />
                  <label
                    htmlFor="confirm-documents"
                    className="cursor-pointer text-sm leading-relaxed text-gray-700"
                  >
                    I understand this is an updated estimate and the final price depends on
                    underwriting.
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {showFooter && (
            <div className="flex justify-end gap-4 pt-4">
              <div className="flex flex-wrap justify-end gap-4">
                {stepStatus !== 'skipped' &&
                  stepStatus !== 'completed' &&
                  sessionType === SessionType.wizard && (
                    <DiscreteActionsDropdown
                      actions={[
                        {
                          id: 'skip',
                          label: 'Skip Step',
                          icon: <WandSparkles className="h-4 w-4" />,
                          onClick: handleSkipStep,
                          disabled: postConsentRecordIsLoading,
                          isLoading: postConsentRecordIsLoading,
                          loadingText: 'Processing...',
                        },
                      ]}
                    />
                  )}
                {hasIllustrationError && (
                  <ContactSupportButton
                    subject="Policy Illustration Error"
                    fallbackEmail="hello@joinwaterlily.com"
                  >
                    Contact Support
                  </ContactSupportButton>
                )}

                {/* Then render primary/secondary buttons */}
                {actions.map((action, index) => (
                  <StepActionButton
                    key={`action-${index}`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    variant={action.type === 'secondary' ? 'outline' : 'default'}
                  >
                    {action.label}
                  </StepActionButton>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
  );
